import React, { FC, useState, useRef, useEffect } from 'react';
import {
  DEFAULT_COLORS,
  DEFAULT_MATRIX,
  NUM_LEVELS_PER_STAGE,
  NUM_LEVELS_TOTAL,
  SHIP_CONFIG,
  TOKEN_ID,
} from '../../traits';
import { music } from '../../audio';
import {
  Main,
  ShipShowcase,
  DialogCutScene,
  StarMap,
  PlanetNFT,
  TextureNFT,
  Win,
} from './scenes';
import { Canvas } from '@react-three/fiber';
import { FullscreenButton, MuteButton } from '../../components';

export type TokenPageProps = {};

const scenes: { [name: string]: FC<any> } = {
  Main,
  ShipShowcase,
  DialogCutScene,
  StarMap,
  PlanetNFT,
  TextureNFT,
  Win,
};

const nextScenes = {
  Main: 'StarMap',
  ShipShowcase: 'DialogCutScene',
  DialogCutScene: 'StarMap',
  StarMap: 'Main',
} as { [k: string]: string };

export const TokenPage: FC<TokenPageProps> = () => {
  const [loading, updateLoading] = useState(true);
  const [matrix, updateMatrix] = useState(DEFAULT_MATRIX);
  const [colors, updateColors] = useState(DEFAULT_COLORS);
  const [{ level, maxLevel, scene }, setLevels] = useState({
    level: 0,
    maxLevel: 0,
    scene: 'ShipShowcase',
  });
  const [fullscreen, setFullscreen] = useState(false);
  const [mute, setMute] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const Scene = scenes[scene];

  useEffect(() => {
    const fetchGlyphData = async () => {
      try {
        let r = await fetch(
          `https://pxg-prod.herokuapp.com/metadata/${TOKEN_ID}`,
        );
        if (r.ok) {
          r = await r.json();
          const { matrix, colors } = r as any;
          updateMatrix(matrix);
          updateColors(colors);
        }
      } catch {}
      updateLoading(false);
    };
    fetchGlyphData();
  }, []);

  useEffect(() => {
    let started = false;
    const startSound = () => {
      if (!started) {
        music.play();
        started = true;
      } else if (!mute && !music.playing) {
        music.play();
      }
    };
    window.addEventListener('keydown', startSound);
    window.addEventListener('click', startSound);
  }, []);

  const aspect = 1.5;

  useEffect(() => {
    if (fullscreen) {
      const exitHandler = () => {
        if (document.fullscreenElement === null) setFullscreen(false);
      };
      document.addEventListener('fullscreenchange', exitHandler);
      return () => {
        document.removeEventListener('fullscreenchange', exitHandler);
      };
    }
    return undefined;
  }, [fullscreen]);

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {loading && <p style={{ color: 'white' }}>Loading</p>}
      {!loading && (
        <div
          style={{
            width: '100vw',
            height: `${100 / aspect}vw`,
            maxHeight: '100vh',
            maxWidth: `${100 * aspect}vh`,
            position: 'relative',
          }}
        >
          <Canvas>
            <Scene
              matrix={matrix}
              shipConfig={SHIP_CONFIG}
              colors={colors}
              level={level}
              maxLevel={level}
              onComplete={(target?: number) => {
                if (scene === 'Main') {
                  if (target === -1) {
                    setLevels({ level: 0, maxLevel: 0, scene: 'ShipShowcase' });
                  } else {
                    setLevels({
                      level: level + 1,
                      maxLevel: Math.max(level + 1, maxLevel),
                      scene:
                        (level + 1) % NUM_LEVELS_PER_STAGE === 0
                          ? 'DialogCutScene'
                          : 'StarMap',
                    });
                  }
                } else if (scene === 'StarMap' && target !== undefined) {
                  setLevels({ level: target, maxLevel, scene: 'Main' });
                } else if (
                  scene === 'DialogCutScene' &&
                  level + 1 > NUM_LEVELS_TOTAL
                ) {
                  setLevels({ level, maxLevel, scene: 'Win' });
                } else {
                  setLevels({ level, maxLevel, scene: nextScenes[scene] });
                }
              }}
            />
          </Canvas>
        </div>
      )}
      <div
        style={{
          position: 'fixed',
          bottom: 8,
          left: 16,
        }}
      >
        <MuteButton
          mute={mute}
          toggle={() => {
            if (mute) {
              setMute(false);
              setTimeout(() => music.play());
            } else {
              setMute(true);
              setTimeout(() => music.pause());
            }
          }}
        />
      </div>
      <div
        style={{
          position: 'fixed',
          bottom: 8,
          right: 16,
        }}
      >
        <FullscreenButton
          fullscreen={fullscreen}
          toggle={() => {
            if (fullscreen) {
              document.exitFullscreen();
              setFullscreen(false);
            } else {
              ref.current?.requestFullscreen();
              setFullscreen(true);
            }
          }}
        />
      </div>
    </div>
  );
};
